import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./HelpHint.module.css";

interface HelpHintProps {
    text: string;
    children?: React.ReactNode;
    className?: string;
}

/** ⓘ help control — always visible; click to open popup (dismiss on outside click or Escape). */
export function HelpHint({ text, children, className }: HelpHintProps) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState<{ top: number; left: number; maxWidth: number } | null>(null);
    const btnRef = useRef<HTMLButtonElement>(null);
    const popupRef = useRef<HTMLSpanElement>(null);

    const close = useCallback(() => setOpen(false), []);

    const toggle = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (open) {
                close();
                return;
            }
            const btn = btnRef.current;
            if (!btn) return;
            const r = btn.getBoundingClientRect();
            const maxWidth = Math.min(320, window.innerWidth - 16);
            const left = Math.min(Math.max(8, r.left), window.innerWidth - maxWidth - 8);
            setPos({ top: r.bottom + 4, left, maxWidth });
            setOpen(true);
        },
        [open, close],
    );

    useEffect(() => {
        if (!open) return;
        const handlePointerDown = (e: PointerEvent) => {
            const target = e.target as Node;
            if (btnRef.current?.contains(target) || popupRef.current?.contains(target)) return;
            close();
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") close();
        };
        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, close]);

    const iconOnly = children == null || children === false || children === "";
    const wrapClass = [styles.wrap, iconOnly && styles.iconOnly, className].filter(Boolean).join(" ");

    return (
        <span className={wrapClass}>
            {children}
            <button
                ref={btnRef}
                type="button"
                className={styles.infoBtn}
                aria-label={`Help: ${text}`}
                onTouchStart={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={toggle}
            >
                ⓘ
            </button>
            {open && pos && (
                <span
                    ref={popupRef}
                    className={styles.popup}
                    style={{ top: pos.top, left: pos.left, maxWidth: pos.maxWidth }}
                    role="tooltip"
                    onClick={(e) => e.stopPropagation()}
                >
                    {text}
                </span>
            )}
        </span>
    );
}
